const MOBILE_BREAKPOINT = 800

export class WebVitals {
  static initialized = false
  static endpoint = '/api/performance/web-vitals/'
  static sampleRate = 1
  static payload = {}
  static clsValue = 0

  static init(options = {}) {
    if (this.initialized) return

    this.endpoint = this.resolveEndpoint(options.endpoint || this.endpoint)
    this.sampleRate = options.sampleRate ?? this.sampleRate

    if (Math.random() > this.sampleRate) return

    this.initialized = true

    this.observeLCP()
    this.observeCLS()
    this.observeINP()

    addEventListener('pagehide', () => this.flush(), { capture: true })
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush()
      }
    })
  }

  static observeLCP() {
    if (!('PerformanceObserver' in window)) return

    try {
      const observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries()
        const lastEntry = entries[entries.length - 1]
        if (!lastEntry) return

        this.setMetric('LCP', lastEntry.startTime, {
          selector: this.getElementSelector(lastEntry.element),
        })
      })

      observer.observe({ type: 'largest-contentful-paint', buffered: true })
    } catch (error) {}
  }

  static observeCLS() {
    if (!('PerformanceObserver' in window)) return

    try {
      const observer = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (!entry.hadRecentInput) {
            this.clsValue += entry.value
          }
        }

        this.setMetric('CLS', this.clsValue)
      })

      observer.observe({ type: 'layout-shift', buffered: true })
    } catch (error) {}
  }

  static observeINP() {
    if (!('PerformanceObserver' in window)) return

    try {
      const observer = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          this.setMetric('INP', entry.duration, {
            selector: this.getElementSelector(entry.target),
            eventType: entry.name || entry.interactionType || 'interaction',
          })
        }
      })

      observer.observe({ type: 'event', buffered: true, durationThreshold: 40 })
    } catch (error) {}
  }

  static setMetric(name, value, meta = {}) {
    if (!Number.isFinite(value)) return

    const previous = this.payload[name]
    if (previous && previous.value >= value) return

    this.payload[name] = {
      name,
      value: Number(value.toFixed(2)),
      rating: this.getRating(name, value),
      url: window.location.href,
      path: window.location.pathname,
      template: document.querySelector('[data-view]')?.dataset?.view || 'unknown',
      deviceClass: window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : 'desktop',
      timestamp: new Date().toISOString(),
      ...meta,
    }
  }

  static getRating(name, value) {
    const thresholds = {
      LCP: { good: 2500, poor: 4000 },
      INP: { good: 200, poor: 500 },
      CLS: { good: 0.1, poor: 0.25 },
    }

    const threshold = thresholds[name]
    if (!threshold) return 'unknown'

    if (value <= threshold.good) return 'good'
    if (value > threshold.poor) return 'poor'
    return 'needs-improvement'
  }

  static getElementSelector(element) {
    if (!element || !(element instanceof Element)) return null

    if (element.id) {
      return `#${element.id}`
    }

    const className = (element.className || '').toString().trim()
    if (className) {
      const classes = className.split(/\s+/).slice(0, 2).join('.')
      return `${element.tagName.toLowerCase()}.${classes}`
    }

    return element.tagName.toLowerCase()
  }

  static flush() {
    const metrics = Object.values(this.payload)
    if (!metrics.length) return

    const body = JSON.stringify({ metrics })
    this.payload = {}

    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon(this.resolveEndpoint(this.endpoint), blob)
      return
    }

    fetch(this.resolveEndpoint(this.endpoint), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: true,
    }).catch(() => {})
  }

  static resolveEndpoint(endpoint) {
    try {
      const resolved = new URL(endpoint, window.location.origin)
      if (window.location.protocol === 'https:' && resolved.protocol === 'http:' && resolved.host === window.location.host) {
        resolved.protocol = 'https:'
      }
      return resolved.toString()
    } catch (error) {
      return endpoint
    }
  }
}
